export type Chapter = {
  id: string;
  title: string;
  pages: string[];
};

export type Comic = {
  id: string;
  title: string;
  author: string;
  cover: string;
  categories: string[];
  region: string;
  status: string;
  latestChapter: string;
  updatedAt: string;
  description: string;
  tags: string[];
  views: number;
  rating: number;
  chapters: Chapter[];
};

export type ComicCategory = {
  id: string;
  name: string;
  icon: string;
  desc: string;
};

const POOL_ACTION: string[] = [
  'https://readdy.ai/api/search-image?query=stylized%20black%20and%20white%20manga%20comic%20page%20with%20dynamic%20action%20panels%20and%20speed%20lines%2C%20high%20contrast%20inking&width=800&height=1200&seq=read-action-01&orientation=portrait',
  'https://readdy.ai/api/search-image?query=stylized%20monochrome%20comic%20page%20with%20intense%20battle%20scene%20panels%2C%20dramatic%20ink%20shading%2C%20graphic%20novel%20style&width=800&height=1200&seq=read-action-02&orientation=portrait',
  'https://readdy.ai/api/search-image?query=stylized%20black%20white%20manga%20page%20with%20character%20close%20up%20panels%20and%20impact%20effects%2C%20clean%20linework&width=800&height=1200&seq=read-action-03&orientation=portrait',
  'https://readdy.ai/api/search-image?query=stylized%20monochrome%20manga%20page%20with%20wide%20landscape%20panel%20and%20dialogue%20bubbles%2C%20detailed%20screentone&width=800&height=1200&seq=read-action-04&orientation=portrait',
];

const POOL_SLICE: string[] = [
  'https://readdy.ai/api/search-image?query=soft%20monochrome%20manga%20page%20with%20gentle%20everyday%20life%20panels%20and%20warm%20expression%2C%20light%20screentone&width=800&height=1200&seq=read-slice-01&orientation=portrait',
  'https://readdy.ai/api/search-image?query=light%20black%20white%20comic%20page%20of%20a%20peaceful%20home%20scene%20with%20characters%20talking%2C%20clean%20minimal%20ink&width=800&height=1200&seq=read-slice-02&orientation=portrait',
  'https://readdy.ai/api/search-image?query=soft%20monochrome%20comic%20page%20with%20close%20up%20smiling%20face%20and%20speech%20bubbles%2C%20delicate%20linework&width=800&height=1200&seq=read-slice-03&orientation=portrait',
  'https://readdy.ai/api/search-image?query=light%20manga%20page%20with%20outdoor%20scenery%20panel%20and%20small%20characters%2C%20airy%20composition&width=800&height=1200&seq=read-slice-04&orientation=portrait',
];

const POOL_FANTASY: string[] = [
  'https://readdy.ai/api/search-image?query=detailed%20monochrome%20manga%20page%20with%20magic%20circle%20and%20glowing%20runes%20panels%2C%20intricate%20ink%20illustration&width=800&height=1200&seq=read-fantasy-01&orientation=portrait',
  'https://readdy.ai/api/search-image?query=black%20and%20white%20fantasy%20comic%20page%20with%20ancient%20ruins%20landscape%20panel%2C%20rich%20detailed%20shading&width=800&height=1200&seq=read-fantasy-02&orientation=portrait',
  'https://readdy.ai/api/search-image?query=monochrome%20comic%20page%20with%20a%20mage%20casting%20spell%20panels%2C%20dramatic%20lighting%20and%20screentone&width=800&height=1200&seq=read-fantasy-03&orientation=portrait',
  'https://readdy.ai/api/search-image?query=detailed%20fantasy%20manga%20page%20with%20dragon%20silhouette%20panel%2C%20highly%20detailed%20ink%20style&width=800&height=1200&seq=read-fantasy-04&orientation=portrait',
];

const POOL_MYSTERY: string[] = [
  'https://readdy.ai/api/search-image?query=moody%20monochrome%20manga%20page%20with%20dark%20alley%20panels%20and%20mysterious%20figure%2C%20heavy%20ink%20shadow&width=800&height=1200&seq=read-mystery-01&orientation=portrait',
  'https://readdy.ai/api/search-image?query=noir%20black%20and%20white%20comic%20page%20with%20close%20up%20of%20suspicious%20eyes%20panels%2C%20tense%20atmosphere&width=800&height=1200&seq=read-mystery-02&orientation=portrait',
  'https://readdy.ai/api/search-image?query=dark%20monochrome%20manga%20page%20with%20city%20skyline%20panels%20and%20rain%20texture%2C%20cinematic%20composition&width=800&height=1200&seq=read-mystery-03&orientation=portrait',
  'https://readdy.ai/api/search-image?query=tense%20black%20white%20comic%20page%20with%20evidence%20board%20panel%20and%20speech%20bubbles%2C%20detailed%20linework&width=800&height=1200&seq=read-mystery-04&orientation=portrait',
];

export const comicCategories: ComicCategory[] = [
  { id: 'action', name: '动作', icon: 'ri-sword-line', desc: '拳拳到肉的热血战斗，肾上腺素拉满的动作名场面。' },
  { id: 'romance', name: '恋爱', icon: 'ri-heart-3-line', desc: '心跳加速的甜蜜日常与虐心羁绊，恋爱漫画精选。' },
  { id: 'fantasy', name: '奇幻', icon: 'ri-magic-line', desc: '魔法、异世界与冒险史诗，带领你遨游幻想之境。' },
  { id: 'blood', name: '热血', icon: 'ri-fire-line', desc: '为梦想燃尽全力的少年，成长与逆袭的高燃故事。' },
  { id: 'suspense', name: '悬疑', icon: 'ri-spy-line', desc: '层层反转的推理与谜案，陪主角一步步揭开真相。' },
  { id: 'campus', name: '校园', icon: 'ri-school-line', desc: '青春校园里的友情、社团与暗恋，回忆杀满满。' },
  { id: 'comedy', name: '搞笑', icon: 'ri-emotion-laugh-line', desc: '沙雕爆笑名场面合集，一键治愈不开心。' },
  { id: 'scifi', name: '科幻', icon: 'ri-rocket-2-line', desc: '未来科技与星际远征，畅想人类的无限可能。' },
  { id: 'ancient', name: '古风', icon: 'ri-leaf-line', desc: '水墨东方与江湖恩怨，画卷般的国风漫画。' },
  { id: 'healing', name: '治愈', icon: 'ri-plant-line', desc: '温柔细腻的暖系故事，抚平每一天的小疲惫。' },
];

export const comics: Comic[] = [
  {
    id: 'c01',
    title: '焰之忍者',
    author: '山田太郎',
    cover: 'https://readdy.ai/api/search-image?query=portrait%20anime%20comic%20cover%20of%20a%20young%20ninja%20surrounded%20by%20orange%20flames%2C%20vivid%20warm%20color%20palette%2C%20clean%20illustration%20style&width=600&height=800&seq=cover-c01&orientation=portrait',
    categories: ['action', 'blood', 'fantasy'],
    region: '日漫',
    status: '连载',
    latestChapter: '第 8 话 · 火之意志',
    updatedAt: '2026-10-12',
    description: '少年忍者岚，背负着被灭门的仇怨，在火之国的试炼中一步步觉醒体内的焰之力量。当他终于找到仇敌的踪迹，却发现真相远比想象中更沉重。热血、羁绊与成长，一部燃到骨子里的忍者物语。',
    tags: ['忍者', '成长', '羁绊', '战斗'],
    views: 1280000,
    rating: 9.2,
    chapters: [
      { id: '1', title: '第 1 话 · 灭门之夜', pages: POOL_ACTION },
      { id: '2', title: '第 2 话 · 试炼之门', pages: POOL_ACTION },
      { id: '3', title: '第 3 话 · 结印修行', pages: POOL_ACTION },
      { id: '4', title: '第 4 话 · 同伴的条件', pages: POOL_ACTION },
      { id: '5', title: '第 5 话 · 初战上忍', pages: POOL_ACTION },
      { id: '6', title: '第 6 话 · 焰之觉醒', pages: POOL_ACTION },
      { id: '7', title: '第 7 话 · 暗流涌动', pages: POOL_ACTION },
      { id: '8', title: '第 8 话 · 火之意志', pages: POOL_ACTION },
    ],
  },
  {
    id: 'c02',
    title: '星海彼岸',
    author: '林晚',
    cover: 'https://readdy.ai/api/search-image?query=portrait%20science%20fiction%20comic%20cover%20of%20a%20girl%20looking%20at%20a%20starry%20galaxy%20and%20spaceship%2C%20deep%20navy%20and%20gold%20tones%2C%20clean%20illustration&width=600&height=800&seq=cover-c02&orientation=portrait',
    categories: ['scifi', 'romance'],
    region: '国漫',
    status: '连载',
    latestChapter: '第 12 话 · 信号回响',
    updatedAt: '2026-10-11',
    description: '公元 2147 年，人类文明在星海中艰难延续。见习领航员苏梨，在一次跃迁事故中收到了来自百年前恋人的信号。跨越星海的等待，究竟是一场幻觉，还是宇宙给她的答案？',
    tags: ['太空', '科幻', '虐恋', '命运'],
    views: 960000,
    rating: 8.8,
    chapters: [
      { id: '1', title: '第 1 话 · 启航', pages: POOL_FANTASY },
      { id: '2', title: '第 2 话 · 跃迁事故', pages: POOL_FANTASY },
      { id: '3', title: '第 3 话 · 陌生信号', pages: POOL_FANTASY },
      { id: '4', title: '第 4 话 · 百年前的信', pages: POOL_FANTASY },
      { id: '5', title: '第 5 话 · 孤独航道', pages: POOL_FANTASY },
      { id: '6', title: '第 6 话 · 引力之约', pages: POOL_FANTASY },
      { id: '7', title: '第 7 话 · 星尘记忆', pages: POOL_FANTASY },
      { id: '8', title: '第 8 话 · 彼岸回声', pages: POOL_FANTASY },
      { id: '9', title: '第 9 话 · 错过的时间', pages: POOL_FANTASY },
      { id: '10', title: '第 10 话 · 破晓坐标', pages: POOL_FANTASY },
      { id: '11', title: '第 11 话 · 最后的讯息', pages: POOL_FANTASY },
      { id: '12', title: '第 12 话 · 信号回响', pages: POOL_FANTASY },
    ],
  },
  {
    id: 'c03',
    title: '校园轻语',
    author: '佐藤樱',
    cover: 'https://readdy.ai/api/search-image?query=portrait%20slice%20of%20life%20anime%20comic%20cover%20of%20high%20school%20students%20under%20cherry%20blossoms%2C%20soft%20pastel%20tones%2C%20clean%20illustration&width=600&height=800&seq=cover-c03&orientation=portrait',
    categories: ['campus', 'comedy', 'romance'],
    region: '日漫',
    status: '完结',
    latestChapter: '第 20 话 · 毕业钟声',
    updatedAt: '2026-09-28',
    description: '一群性格迥异的高中生，在樱花纷飞的校园里度过爆笑又温暖的三年。社团、考试、暗恋与离别，每一格都写满了青春。最治愈的校园群像喜剧，正式完结。',
    tags: ['青春', '社团', '搞笑', '日常'],
    views: 720000,
    rating: 8.5,
    chapters: [
      { id: '1', title: '第 1 话 · 开学第一天', pages: POOL_SLICE },
      { id: '2', title: '第 2 话 · 社团招新', pages: POOL_SLICE },
      { id: '3', title: '第 3 话 · 便当风波', pages: POOL_SLICE },
      { id: '4', title: '第 4 话 · 文化祭前夜', pages: POOL_SLICE },
      { id: '5', title: '第 5 话 · 天台告白', pages: POOL_SLICE },
      { id: '6', title: '第 6 话 · 期末考试', pages: POOL_SLICE },
    ],
  },
  {
    id: 'c04',
    title: '长安幻夜',
    author: '墨白',
    cover: 'https://readdy.ai/api/search-image?query=portrait%20chinese%20ancient%20fantasy%20comic%20cover%20of%20a%20scholar%20in%20traditional%20robe%20under%20moonlight%20lanterns%2C%20ink%20wash%20style%20with%20warm%20accents&width=600&height=800&seq=cover-c04&orientation=portrait',
    categories: ['ancient', 'fantasy', 'suspense'],
    region: '国漫',
    status: '连载',
    latestChapter: '第 15 话 · 灯下迷局',
    updatedAt: '2026-10-10',
    description: '盛唐长安，坊市之间暗藏精怪。少年画师沈砚能看见常人看不见的「夜影」，他与神秘女捕快联手，一桩桩离奇命案的背后，竟是百年前一场被抹去的往事。',
    tags: ['国风', '精怪', '悬疑', '长安'],
    views: 880000,
    rating: 9.0,
    chapters: [
      { id: '1', title: '第 1 话 · 夜市异闻', pages: POOL_MYSTERY },
      { id: '2', title: '第 2 话 · 画中之人', pages: POOL_MYSTERY },
      { id: '3', title: '第 3 话 · 女捕快', pages: POOL_MYSTERY },
      { id: '4', title: '第 4 话 · 失踪的绣娘', pages: POOL_MYSTERY },
      { id: '5', title: '第 5 话 · 影中之影', pages: POOL_MYSTERY },
    ],
  },
  {
    id: 'c05',
    title: '深海回响',
    author: '金秀雅',
    cover: 'https://readdy.ai/api/search-image?query=portrait%20thriller%20comic%20cover%20of%20a%20diver%20descending%20into%20a%20dark%20deep%20sea%20trench%2C%20teal%20and%20dark%20tones%2C%20cinematic%20illustration&width=600&height=800&seq=cover-c05&orientation=portrait',
    categories: ['suspense', 'scifi'],
    region: '韩漫',
    status: '连载',
    latestChapter: '第 9 话 · 万米之下',
    updatedAt: '2026-10-09',
    description: '深海科考队在马里亚纳海沟探测到一段有规律的声波信号。潜水员韩雪带着疑问下潜，却在万米之下发现了不该存在的东西。人类对深海的了解，或许还不及对太空。',
    tags: ['深海', '悬疑', '生存', '科幻'],
    views: 610000,
    rating: 8.9,
    chapters: [
      { id: '1', title: '第 1 话 · 异常信号', pages: POOL_MYSTERY },
      { id: '2', title: '第 2 话 · 下潜', pages: POOL_MYSTERY },
      { id: '3', title: '第 3 话 · 黑暗中的轮廓', pages: POOL_MYSTERY },
      { id: '4', title: '第 4 话 · 失联', pages: POOL_MYSTERY },
      { id: '5', title: '第 5 话 · 万米之下', pages: POOL_MYSTERY },
    ],
  },
  {
    id: 'c06',
    title: '猫咪日常',
    author: '小林悠',
    cover: 'https://readdy.ai/api/search-image?query=portrait%20healing%20comic%20cover%20of%20a%20fluffy%20cat%20napping%20on%20a%20sunny%20windowsill%2C%20soft%20warm%20cream%20tones%2C%20cute%20illustration&width=600&height=800&seq=cover-c06&orientation=portrait',
    categories: ['healing', 'comedy'],
    region: '日漫',
    status: '连载',
    latestChapter: '第 6 话 · 纸箱战争',
    updatedAt: '2026-10-08',
    description: '上班族与一只傲娇橘猫的同居日常。喂食、铲屎、被嫌弃，却又被深夜的一只毛茸茸爪子治愈。每一话都能让你会心一笑的暖系萌宠漫画。',
    tags: ['萌宠', '日常', '治愈', '搞笑'],
    views: 540000,
    rating: 9.1,
    chapters: [
      { id: '1', title: '第 1 话 · 相遇', pages: POOL_SLICE },
      { id: '2', title: '第 2 话 · 铲屎官的自觉', pages: POOL_SLICE },
      { id: '3', title: '第 3 话 · 深夜跑酷', pages: POOL_SLICE },
      { id: '4', title: '第 4 话 · 晒太阳', pages: POOL_SLICE },
      { id: '5', title: '第 5 话 · 纸箱战争', pages: POOL_SLICE },
      { id: '6', title: '第 6 话 · 一起去海边', pages: POOL_SLICE },
    ],
  },
  {
    id: 'c07',
    title: '剑起苍穹',
    author: '风吟',
    cover: 'https://readdy.ai/api/search-image?query=portrait%20chinese%20wuxia%20comic%20cover%20of%20a%20swordsman%20standing%20on%20a%20cliff%20with%20flying%20sword%2C%20ink%20and%20gold%20tones%2C%20epic%20illustration&width=600&height=800&seq=cover-c07&orientation=portrait',
    categories: ['blood', 'ancient', 'action'],
    region: '国漫',
    status: '连载',
    latestChapter: '第 18 话 · 一剑破天',
    updatedAt: '2026-10-11',
    description: '少年萧凡从剑冢中拔出一柄断剑，自此踏上修剑之路。师门被灭、挚友背叛，他握紧剑柄，要让这苍穹为之低头。东方玄幻爽文漫改代表作。',
    tags: ['修仙', '热血', '爽文', '剑道'],
    views: 1520000,
    rating: 8.7,
    chapters: [
      { id: '1', title: '第 1 话 · 剑冢', pages: POOL_ACTION },
      { id: '2', title: '第 2 话 · 断剑', pages: POOL_ACTION },
      { id: '3', title: '第 3 话 · 入门', pages: POOL_ACTION },
      { id: '4', title: '第 4 话 · 同门', pages: POOL_ACTION },
      { id: '5', title: '第 5 话 · 初雪试剑', pages: POOL_ACTION },
      { id: '6', title: '第 6 话 · 一剑破天', pages: POOL_ACTION },
    ],
  },
  {
    id: 'c08',
    title: '心动信号',
    author: '苏念',
    cover: 'https://readdy.ai/api/search-image?query=portrait%20romance%20comic%20cover%20of%20a%20shy%20couple%20sharing%20earphones%20in%20classroom%2C%20soft%20pink%20and%20cream%20tones%2C%20sweet%20illustration&width=600&height=800&seq=cover-c08&orientation=portrait',
    categories: ['romance', 'campus'],
    region: '国漫',
    status: '完结',
    latestChapter: '第 16 话 · 我们的夏天',
    updatedAt: '2026-09-20',
    description: '转学生许星遇见了沉默寡言的学霸顾言。一次同桌、一副耳机、一个共享的秘密，两颗心慢慢靠近。青春里最干净的心动，就在这里。',
    tags: ['甜宠', '暗恋', '校园', '青春'],
    views: 690000,
    rating: 8.3,
    chapters: [
      { id: '1', title: '第 1 话 · 转学生', pages: POOL_SLICE },
      { id: '2', title: '第 2 话 · 同桌协议', pages: POOL_SLICE },
      { id: '3', title: '第 3 话 · 同一副耳机', pages: POOL_SLICE },
      { id: '4', title: '第 4 话 · 雨季', pages: POOL_SLICE },
      { id: '5', title: '第 5 话 · 心动的信号', pages: POOL_SLICE },
      { id: '6', title: '第 6 话 · 我们的夏天', pages: POOL_SLICE },
    ],
  },
  {
    id: 'c09',
    title: '末日方舟',
    author: '陈默',
    cover: 'https://readdy.ai/api/search-image?query=portrait%20post%20apocalyptic%20comic%20cover%20of%20a%20lone%20survivor%20before%20a%20giant%20ark%20in%20ruins%2C%20dusty%20orange%20and%20grey%20tones%2C%20cinematic%20illustration&width=600&height=800&seq=cover-c09&orientation=portrait',
    categories: ['scifi', 'action', 'suspense'],
    region: '国漫',
    status: '连载',
    latestChapter: '第 11 话 · 最后的人类',
    updatedAt: '2026-10-07',
    description: '病毒爆发十年后，幸存者陈舟带着妹妹寻找传说中的「方舟」。废土之上，比丧尸更危险的是人心。一场关于希望与人性抉择的公路冒险。',
    tags: ['末世', '生存', '兄妹', '冒险'],
    views: 830000,
    rating: 8.6,
    chapters: [
      { id: '1', title: '第 1 话 · 十年之后', pages: POOL_MYSTERY },
      { id: '2', title: '第 2 话 · 加油站', pages: POOL_MYSTERY },
      { id: '3', title: '第 3 话 · 同伴', pages: POOL_MYSTERY },
      { id: '4', title: '第 4 话 · 背叛', pages: POOL_MYSTERY },
      { id: '5', title: '第 5 话 · 方舟坐标', pages: POOL_MYSTERY },
      { id: '6', title: '第 6 话 · 最后的人类', pages: POOL_MYSTERY },
    ],
  },
  {
    id: 'c10',
    title: '精灵物语',
    author: '川岛美咲',
    cover: 'https://readdy.ai/api/search-image?query=portrait%20fantasy%20comic%20cover%20of%20a%20tiny%20forest%20spirit%20holding%20a%20glowing%20seed%2C%20lush%20green%20and%20gold%20tones%2C%20whimsical%20illustration&width=600&height=800&seq=cover-c10&orientation=portrait',
    categories: ['fantasy', 'healing'],
    region: '日漫',
    status: '连载',
    latestChapter: '第 7 话 · 会说话的种子',
    updatedAt: '2026-10-06',
    description: '森林深处住着一群守护自然的小精灵。胆小的小精灵阿栗捡到一颗会说话的种子，为了把它送回故乡，踏上了意想不到的旅程。治愈系的童话冒险。',
    tags: ['童话', '精灵', '冒险', '治愈'],
    views: 470000,
    rating: 8.4,
    chapters: [
      { id: '1', title: '第 1 话 · 阿栗', pages: POOL_FANTASY },
      { id: '2', title: '第 2 话 · 会说话的种子', pages: POOL_FANTASY },
      { id: '3', title: '第 3 话 · 迷雾森林', pages: POOL_FANTASY },
      { id: '4', title: '第 4 话 · 河神', pages: POOL_FANTASY },
      { id: '5', title: '第 5 话 · 故乡的方向', pages: POOL_FANTASY },
      { id: '6', title: '第 6 话 · 新的旅程', pages: POOL_FANTASY },
      { id: '7', title: '第 7 话 · 森林的心跳', pages: POOL_FANTASY },
    ],
  },
  {
    id: 'c11',
    title: '城市猎人1968',
    author: '李承勋',
    cover: 'https://readdy.ai/api/search-image?query=portrait%20noir%20comic%20cover%20of%20a%20private%20detective%20in%20a%20rainy%20seoul%20street%2C%20moody%20blue%20and%20amber%20tones%2C%20cinematic%20illustration&width=600&height=800&seq=cover-c11&orientation=portrait',
    categories: ['action', 'suspense'],
    region: '韩漫',
    status: '连载',
    latestChapter: '第 14 话 · 雨夜枪声',
    updatedAt: '2026-10-12',
    description: '1968 年的首尔，私家侦探姜武受雇调查一桩失踪案，却卷入政商勾结的巨大阴谋。他握着老式左轮，在这座霓虹与雨水交织的城市里寻找真相。',
    tags: ['侦探', '犯罪', '年代', '硬汉'],
    views: 990000,
    rating: 9.3,
    chapters: [
      { id: '1', title: '第 1 话 · 委托', pages: POOL_MYSTERY },
      { id: '2', title: '第 2 话 · 雨夜车票', pages: POOL_MYSTERY },
      { id: '3', title: '第 3 话 · 夜总会', pages: POOL_MYSTERY },
      { id: '4', title: '第 4 话 · 消失的证人', pages: POOL_MYSTERY },
      { id: '5', title: '第 5 话 · 雨夜枪声', pages: POOL_MYSTERY },
    ],
  },
  {
    id: 'c12',
    title: '花间辞',
    author: '白鹿',
    cover: 'https://readdy.ai/api/search-image?query=portrait%20chinese%20ancient%20romance%20comic%20cover%20of%20a%20lady%20in%20hanfu%20among%20blooming%20flowers%2C%20soft%20pink%20and%20jade%20tones%2C%20delicate%20illustration&width=600&height=800&seq=cover-c12&orientation=portrait',
    categories: ['ancient', 'romance'],
    region: '国漫',
    status: '连载',
    latestChapter: '第 10 话 · 宫墙柳',
    updatedAt: '2026-10-05',
    description: '江南花商之女陆辞，因一纸婚约踏入深宫。她不争不抢，只愿守着一方花圃，却偏偏被卷入帝王与前朝的旧账。一朵花，一段情，一阙辞。',
    tags: ['古言', '宫斗', '虐恋', '唯美'],
    views: 760000,
    rating: 8.8,
    chapters: [
      { id: '1', title: '第 1 话 · 花商之女', pages: POOL_FANTASY },
      { id: '2', title: '第 2 话 · 一纸婚约', pages: POOL_FANTASY },
      { id: '3', title: '第 3 话 · 入宫', pages: POOL_FANTASY },
      { id: '4', title: '第 4 话 · 花圃', pages: POOL_FANTASY },
      { id: '5', title: '第 5 话 · 宫墙柳', pages: POOL_FANTASY },
    ],
  },
  {
    id: 'c13',
    title: '篮球少年',
    author: '周燃',
    cover: 'https://readdy.ai/api/search-image?query=portrait%20sports%20comic%20cover%20of%20a%20teenager%20dunking%20a%20basketball%20in%20a%20school%20gym%2C%20vibrant%20orange%20and%20white%20tones%2C%20dynamic%20illustration&width=600&height=800&seq=cover-c13&orientation=portrait',
    categories: ['blood', 'campus'],
    region: '国漫',
    status: '连载',
    latestChapter: '第 13 话 · 加时赛',
    updatedAt: '2026-10-04',
    description: '身高不足却弹跳惊人的高中生林冲，凭一股不服输的劲闯入校队。从板凳到首发，从被质疑到被信赖，他要带领这支落魄球队打进全国大赛。',
    tags: ['运动', '篮球', '热血', '团队'],
    views: 580000,
    rating: 8.2,
    chapters: [
      { id: '1', title: '第 1 话 · 被拒绝的人', pages: POOL_ACTION },
      { id: '2', title: '第 2 话 · 加练', pages: POOL_ACTION },
      { id: '3', title: '第 3 话 · 首发之争', pages: POOL_ACTION },
      { id: '4', title: '第 4 话 · 新队友', pages: POOL_ACTION },
      { id: '5', title: '第 5 话 · 加时赛', pages: POOL_ACTION },
    ],
  },
  {
    id: 'c14',
    title: '魔法学院日志',
    author: '高桥凛',
    cover: 'https://readdy.ai/api/search-image?query=portrait%20magic%20academy%20comic%20cover%20of%20students%20casting%20spells%20in%20a%20grand%20hall%2C%20purple%20and%20gold%20magical%20glow%2C%20detailed%20illustration&width=600&height=800&seq=cover-c14&orientation=portrait',
    categories: ['fantasy', 'campus', 'comedy'],
    region: '日漫',
    status: '连载',
    latestChapter: '第 9 话 · 期末大考',
    updatedAt: '2026-10-03',
    description: '成绩垫底的魔法生小野寺，误打误撞召唤出了一只只会吐槽的迷你龙。从此他的学院生活被各种炸裂的魔法事故填满。爆笑奇幻校园日常。',
    tags: ['魔法', '校园', '搞笑', '伙伴'],
    views: 640000,
    rating: 8.9,
    chapters: [
      { id: '1', title: '第 1 话 · 入学考试', pages: POOL_FANTASY },
      { id: '2', title: '第 2 话 · 迷你龙', pages: POOL_FANTASY },
      { id: '3', title: '第 3 话 · 魔法事故', pages: POOL_FANTASY },
      { id: '4', title: '第 4 话 · 图书馆禁书', pages: POOL_FANTASY },
      { id: '5', title: '第 5 话 · 期末大考', pages: POOL_FANTASY },
    ],
  },
  {
    id: 'c15',
    title: '白夜迷案',
    author: '郑雨珍',
    cover: 'https://readdy.ai/api/search-image?query=portrait%20crime%20thriller%20comic%20cover%20of%20a%20female%20detective%20examining%20a%20crime%20scene%20at%20night%2C%20cool%20grey%20and%20blue%20tones%2C%20cinematic%20illustration&width=600&height=800&seq=cover-c15&orientation=portrait',
    categories: ['suspense', 'action'],
    region: '韩漫',
    status: '完结',
    latestChapter: '第 22 话 · 真相大白',
    updatedAt: '2026-09-15',
    description: '在极昼的白夜里，连环失踪案接连发生。刑警车恩京抽丝剥茧，发现所有线索都指向二十年前的一桩旧案。完结悬疑神作，一口气看完全程高能。',
    tags: ['推理', '刑侦', '反转', '完结'],
    views: 1080000,
    rating: 9.0,
    chapters: [
      { id: '1', title: '第 1 话 · 极昼', pages: POOL_MYSTERY },
      { id: '2', title: '第 2 话 · 第一具尸体', pages: POOL_MYSTERY },
      { id: '3', title: '第 3 话 · 二十年前', pages: POOL_MYSTERY },
      { id: '4', title: '第 4 话 · 嫌疑人们', pages: POOL_MYSTERY },
      { id: '5', title: '第 5 话 · 真相大白', pages: POOL_MYSTERY },
    ],
  },
  {
    id: 'c16',
    title: '山海食堂',
    author: '阿禾',
    cover: 'https://readdy.ai/api/search-image?query=portrait%20cozy%20comic%20cover%20of%20a%20small%20food%20stall%20with%20steaming%20dishes%20and%20a%20cute%20beast%20customer%2C%20warm%20orange%20and%20cream%20tones%2C%20charming%20illustration&width=600&height=800&seq=cover-c16&orientation=portrait',
    categories: ['healing', 'fantasy'],
    region: '国漫',
    status: '连载',
    latestChapter: '第 8 话 · 龙王的宵夜',
    updatedAt: '2026-10-09',
    description: '深夜的山海街角，开着一家只招待精怪的食堂。老板阿禾用一道道家常菜，听客人讲述他们的故事。食物与神话交织的温暖治愈物语。',
    tags: ['美食', '神话', '治愈', '日常'],
    views: 520000,
    rating: 9.1,
    chapters: [
      { id: '1', title: '第 1 话 · 深夜开张', pages: POOL_SLICE },
      { id: '2', title: '第 2 话 · 迷路的狐狸', pages: POOL_SLICE },
      { id: '3', title: '第 3 话 · 一碗汤面', pages: POOL_SLICE },
      { id: '4', title: '第 4 话 · 龙王的宵夜', pages: POOL_SLICE },
      { id: '5', title: '第 5 话 · 归乡的客人', pages: POOL_SLICE },
    ],
  },
  {
    id: 'c17',
    title: '龙脉觉醒',
    author: '玄夜',
    cover: 'https://readdy.ai/api/search-image?query=portrait%20action%20fantasy%20comic%20cover%20of%20a%20warrior%20surrounded%20by%20glowing%20dragon%20aura%2C%20red%20and%20gold%20tones%2C%20epic%20illustration&width=600&height=800&seq=cover-c17&orientation=portrait',
    categories: ['action', 'blood', 'fantasy'],
    region: '国漫',
    status: '连载',
    latestChapter: '第 17 话 · 龙魂共鸣',
    updatedAt: '2026-10-10',
    description: '平凡少年秦野体内沉睡着上古龙脉之力。当异族入侵、家国危难，他必须唤醒血脉中的巨龙。热血燃魂的东方战斗漫画，画风炸裂。',
    tags: ['龙族', '战斗', '觉醒', '热血'],
    views: 910000,
    rating: 8.5,
    chapters: [
      { id: '1', title: '第 1 话 · 沉睡的血脉', pages: POOL_ACTION },
      { id: '2', title: '第 2 话 · 异族入侵', pages: POOL_ACTION },
      { id: '3', title: '第 3 话 · 初次觉醒', pages: POOL_ACTION },
      { id: '4', title: '第 4 话 · 龙鳞', pages: POOL_ACTION },
      { id: '5', title: '第 5 话 · 龙魂共鸣', pages: POOL_ACTION },
    ],
  },
  {
    id: 'c18',
    title: '失忆恋人',
    author: '朴敏',
    cover: 'https://readdy.ai/api/search-image?query=portrait%20romance%20mystery%20comic%20cover%20of%20a%20woman%20and%20man%20standing%20apart%20in%20soft%20rain%2C%20muted%20rose%20and%20grey%20tones%2C%20emotional%20illustration&width=600&height=800&seq=cover-c18&orientation=portrait',
    categories: ['romance', 'suspense'],
    region: '韩漫',
    status: '连载',
    latestChapter: '第 9 话 · 被删掉的记忆',
    updatedAt: '2026-10-02',
    description: '一场车祸后，智秀失去了三年的记忆，而守在她身边的男人，自称是她的未婚夫。可每当她想靠近，脑海中就闪过模糊的警告。他到底是谁？',
    tags: ['失忆', '悬疑', '虐恋', '反转'],
    views: 430000,
    rating: 8.1,
    chapters: [
      { id: '1', title: '第 1 话 · 空白三年', pages: POOL_MYSTERY },
      { id: '2', title: '第 2 话 · 未婚夫', pages: POOL_MYSTERY },
      { id: '3', title: '第 3 话 · 似曾相识', pages: POOL_MYSTERY },
      { id: '4', title: '第 4 话 · 一张旧照片', pages: POOL_MYSTERY },
      { id: '5', title: '第 5 话 · 被删掉的记忆', pages: POOL_MYSTERY },
    ],
  },
];

export type BannerItem = {
  id: string;
  comicId: string;
  image: string;
  subtitle: string;
  slogan: string;
};

export const banners: BannerItem[] = [
  {
    id: 'b1',
    comicId: 'c11',
    image:
      'https://readdy.ai/api/search-image?query=cinematic%20wide%20banner%20illustration%20of%20a%20noir%20detective%20walking%20a%20rainy%20neon%20city%20street%20at%20night%2C%20moody%20amber%20and%20teal%20tones%2C%20dramatic%20lighting%2C%20artistic%20comic%20style&width=1920&height=720&seq=banner-c11&orientation=landscape',
    subtitle: '韩漫人气第一 · 硬派推理',
    slogan: '雨夜之下，谁在说谎',
  },
  {
    id: 'b2',
    comicId: 'c07',
    image:
      'https://readdy.ai/api/search-image?query=cinematic%20wide%20banner%20illustration%20of%20an%20eastern%20swordsman%20on%20a%20misty%20cliff%20with%20a%20glowing%20flying%20sword%2C%20ink%20wash%20and%20gold%20accents%2C%20epic%20atmosphere&width=1920&height=720&seq=banner-c07&orientation=landscape',
    subtitle: '年度最燃国漫 · 持续霸榜',
    slogan: '一剑破天，苍穹为谁低',
  },
  {
    id: 'b3',
    comicId: 'c01',
    image:
      'https://readdy.ai/api/search-image?query=cinematic%20wide%20banner%20illustration%20of%20a%20young%20ninja%20leaping%20through%20orange%20flames%20in%20a%20burning%20forest%2C%20dynamic%20warm%20color%20palette%2C%20dramatic%20comic%20style&width=1920&height=720&seq=banner-c01&orientation=landscape',
    subtitle: '日漫热血经典 · 亿万阅读',
    slogan: '火之意志，永不熄灭',
  },
  {
    id: 'b4',
    comicId: 'c12',
    image:
      'https://readdy.ai/api/search-image?query=cinematic%20wide%20banner%20illustration%20of%20an%20elegant%20lady%20in%20hanfu%20standing%20in%20a%20sea%20of%20pink%20blossoms%20under%20moonlight%2C%20soft%20rose%20and%20jade%20tones%2C%20dreamy%20artistic%20style&width=1920&height=720&seq=banner-c12&orientation=landscape',
    subtitle: '唯美古言连载 · 高甜预警',
    slogan: '花间一辞，不负相思',
  },
];

export type RankingItem = { rank: number; comicId: string; score: number };

export const monthlyRanking: RankingItem[] = [
  { rank: 1, comicId: 'c11', score: 9832 },
  { rank: 2, comicId: 'c07', score: 9611 },
  { rank: 3, comicId: 'c01', score: 9402 },
  { rank: 4, comicId: 'c15', score: 9188 },
  { rank: 5, comicId: 'c04', score: 8976 },
  { rank: 6, comicId: 'c17', score: 8742 },
  { rank: 7, comicId: 'c02', score: 8530 },
  { rank: 8, comicId: 'c16', score: 8310 },
  { rank: 9, comicId: 'c14', score: 8095 },
  { rank: 10, comicId: 'c06', score: 7880 },
];

export const hotComicIds = ['c11', 'c07', 'c01', 'c15', 'c04', 'c17'];
export const newComicIds = ['c02', 'c16', 'c14', 'c05', 'c18', 'c09'];
export const editorPicks = ['c04', 'c16', 'c11', 'c12', 'c01', 'c15'];

export const regions = ['全部', '日漫', '国漫', '韩漫', '欧美'];
export const statuses = ['全部', '连载', '完结'];
export const sortOptions = ['综合排序', '更新时间', '人气最高', '评分最高'];